/* eslint-disable @typescript-eslint/no-empty-interface */
import type { AiTaskStatus, AssetType } from '@ve-pro/enums/asset'
import {
  Bool,
  SegmentType,
  SourceSegmentIdBizType,
} from '@ve-pro/enums/segment'
import {
  FontStyle,
  FontWeight,
  TextAlign,
  TextDecoration,
  TextResize,
} from '@ve-pro/enums/text-property'
import { TrackMode, TrackType } from '@ve-pro/enums/track-type'
import {
  Segment,
  Track,
  RatioTypeUnion,
  SegmentRatios,
  SegmentMaterial,
  SegmentCollection,
  SegmentText,
  SegmentVideo,
} from '@ve-pro/types/track.type'
import { ProjectAssetsModel, AsssetsModel } from '../aside/type'
import { Orientation } from 'video-editor-renderer'
import type { AssetGroup } from '../asset-group/type'
import { ExportStatus } from '@ve-pro/config/export'
import type { ProjectMode, ProjectType } from '@ve-pro/enums/project'
import { UserPlan } from '@pt/types/user.type'
import type { Resolution } from '@ve-pro/ve/utils/ratios'
import type {
  AuditStatus,
  TemplateType,
  ShowStatus,
} from '@ve-pro/enums/template'
import { AiTaskModel, SpeechVoiceModel } from '@/api/ai/index.type'
import type { SegmentFragmentsResource } from '../template/type'
import type { Rect } from '@ve-pro/types/index.type'
import { Task } from '@ve-pro/replace/components/task-manager/types'
import { Gender } from '@com/components/speaker-gender/utils'
import { SpeakerType } from '@ve-pro/enums/speaker'
import { VoiceItem } from '@pt/api/ai/index.type'
import { TranslateError } from '@ve-pro/replace/components/languages-fission-manager/enums'

export interface ProjectTrack<T> {
  id: string
  // 轨道类型
  type: TrackType
  // 该字段为片段元件 ID，有值并且>0时候，表示片段轨道
  fragment_segment_id?: Segment['id']
  // 段集合
  segments: T
  // 该轨道是否禁音
  mute: boolean
  // 是否隐藏轨道
  is_hide: boolean
  // 主轨道标识
  is_main: boolean
}

/**
 * 接口返回到动画资源数据模型
 */
export interface AnimationAssetModel extends AsssetsModel {}

/**
 * 项目详情元件中动画的模型
 */
export interface ProjectSegmentAnimation {
  id: number
  asset_id?: number // !!!该字段数据，是后端对剧本元件的动画数据进行了添加处理，好让前端进行判断使用
  duration: number
  deprecated?: boolean
}

/**
 * 前端数据交互使用的动画数据模型
 */
export interface Animation
  extends ProjectSegmentAnimation,
    AnimationAssetModel {}

export interface ProjectSegmentModel {
  // 元件id
  id: number
  // 元件类型
  type: SegmentType

  // 区分为「视频编辑」与「字幕与配音」两个环节
  stage: ProjectMode

  // 元件引用资源id ve_user_asset 表中的资源id
  asset_id: ProjectAssetsModel['asset_id']
  asset_type: ProjectAssetsModel['type']

  // 存储资源组id
  asset_group_id: AssetGroup['id']

  // 元件引用资源id resources 表中的资源id
  res_id: number

  // 进场时间
  start_time: number
  // 资源从左边截取的开始时间，单位毫秒
  trim_start_time: number
  // 持续时间 豪秒
  duration: number

  // 变速后的帧信息（帧版本迭代后才存在的字段）
  start_frame?: null | number
  trim_start_frame?: null | number
  duration_frames?: null | number
  trim_start_frame_rate?: null | number
  duration_frames_rate?: null | number

  // 元件名称（从素材名称复制过来）
  name: string

  // 缩放值x
  scale_x: number
  // 缩放值y
  scale_y: number
  // 元件位置x
  x: number
  // 元件位置y
  y: number
  // 元件旋转角度，值为45是45度
  rotation: number

  // 表中的资源id
  ve_user_asset: string
  // 背景颜色
  background_background: string
  // 透明度
  opacity: number
  // 关联项目id
  project_id: number

  // 字体颜色
  color: string
  // 字体大小
  font_size: number
  // 字体样式，italic | normal
  font_style: FontStyle
  // 加粗，bord | normal
  font_weight: FontWeight
  // 行间距，0.2
  line_spacing: number
  // 字间隔
  letter_spacing: number
  // 文本内容
  text: string
  // 对齐方式，left | right | center
  text_align: TextAlign
  // 文本输入宽高适配方式
  text_resize: TextResize
  // 下划线，underline | none
  text_decoration: TextDecoration
  // 描边粗细
  text_stroke_width: number
  // 背景颜色
  background_color: string
  // 背景透明度
  background_opacity: number
  width: number
  height: number
  // 描边颜色
  text_stroke_color: string

  // 透明度
  alpha: number

  // 音量
  volume: number

  // 是否已经分离音频
  audio_separated: boolean

  // 淡入持续时间
  fade_in_duration: number
  // 淡出持续时间
  fade_out_duration: number

  // 阴影颜色
  shadow_color: string

  // 阴影模度(0%~100%)
  shadow_blur: number

  // 阴影距离（x&y）
  shadow_x: number
  shadow_y: number

  // 字体
  font_id: number

  // 音频/视频播放速率（0.1x / 1x / 2x / 5x / 10x / max）max只为100x，max倍数为片段只有0.1s时换算的倍速
  rate: number

  // 锁定缩放比例
  lock_scale: boolean

  // 音频变调 true为保持原调，false为变调
  preserves_pitch: boolean

  // 扣图
  enable_matting: boolean
  // 扣图颜色
  matting_color: string
  // 扣图强度
  matting_strength: number
  // 扣图羽化
  matting_feather: number

  // 入场动画
  anim_in: ProjectSegmentAnimation | null
  // 出场动画
  anim_out: ProjectSegmentAnimation | null
  // 混合动画
  anim_inout: ProjectSegmentAnimation | null
  // 循环动画
  anim_loop: ProjectSegmentAnimation | null

  // 滤镜强度 0~100
  uni_alpha: number

  // 花字样式 里面的值不确定
  flower_style: any
  before_transition_id: Segment['id']
  behind_transition_id: Segment['id']

  // pid>0的元件是元件组下的子元件，pid为父元件组的id。pid相同的元件属于相同元件组。
  pid: number

  // 水印
  drift_speed: number

  // 标识该元件组是否时序叉乘
  multi_cross: boolean

  // 多尺寸
  ratios: SegmentRatios | null

  // 静音
  mute: boolean

  // 元件归属语言
  lang: string

  // 元件来源于哪个元件，比如视频生成的多个文本元件，文本生成的音频元件
  source_segment_id: number
  // source_segment_id 关联的业务场景枚举
  biz_type?: SourceSegmentIdBizType | undefined

  // 关联的片段元件
  fragment_segment_id: ProjectSegmentModel['id']
  // 片段元件记录视频片段的id，可以根据这个id同步更改
  // 需要根据接口定义进行关联
  fragment_id?: SegmentFragmentsResource['fragment_id']

  // 音频识别角色字段
  speaker: string

  // 文本自动适应宽度缩放字体大小
  auto_font_size: boolean

  source: string

  // 坑位名称
  pit_location?: {
    name: string
    name_en: string
  }
}

export type TemplatePreviews = Partial<Record<RatioTypeUnion, string>>

export interface TemplateModel {
  /** 创建者id */
  adv_id: number
  /** 模板审核状态 */
  audit_status: AuditStatus
  /** 复制模板id */
  copy_template_id: number
  /** 模板视频封面图 */
  cover_url: string
  id: number
  /** 模板是否支持替换 */
  is_replaceable: Bool
  /** 模板名称 */
  name: string
  /** 模板英文名称 */
  name_en: string
  previews: TemplatePreviews | null
  /** 关联源项目id */
  project_id: number
  /** 替换图片数 */
  replace_image_count: number
  /** 替换文本数 */
  replace_text_count: number
  /** 替换视频数 */
  replace_video_count: number
  /** 上架状态 */
  show_status: ShowStatus
  /** 模板类型 */
  type: TemplateType
  /** 模板标签 */
  tag_ids: string
  /** 模板行业 */
  industry_ids: string
  /** 模板封面 */
  cover_previews: string
  created_time: number
  deleted_time: number
  updated_time: number
}

export interface ProjectModel {
  // 是否是模板
  is_template: Bool
  // 项目锁定状态
  locked: Bool
  last_stage: ProjectMode
  track_mode: TrackMode
  // 广告主id
  adv_id: number

  // 项目封面
  cover_url: string

  // 创建时间
  created_time: number

  // 删除时间，考虑到以后可能有回收站之类的功能，所以标记删除
  deleted_time: number

  // 视频时长，单位毫秒
  duration: number
  // 主键ID
  id: number

  // 项目名称
  name: string

  // 项目比例，比如 9:16
  ratio: RatioTypeUnion | ''
  // 当前视频支持的比例 如：16:9,9:16
  ratios: string

  tracks: ProjectTrack<ProjectSegmentModel[] | null>[]

  // 更新时间
  updated_time: number

  // 画布适配方式
  orientation_adapter: Orientation

  product_id: number
  product_name: string

  // 项目语言
  language: string

  // 项目类型
  type: ProjectType

  template: TemplateModel | null

  // 多语言
  languages: string

  config: string

  // 导出数
  export_count: number
  // 导出任务数
  task_count: number
  // 批量组合数
  group_total: number
  // 创意主尺寸
  main_ratio: string
}

export interface ProjectProgressItem {
  // '用户id'
  adv_id: number
  // 码率
  bit_rate: number
  // 创建时间
  created_time: number
  // 删除时间
  deleted_time: number
  // 资源时长
  duration: number
  // '文件md5'
  file_md5: string
  // '文件大小'
  file_size: number
  // '文件地址'
  file_url: string
  // '帧率'
  frame_frame: number
  // '视频高'
  height: number
  // '自增id'
  id: number
  // '渲染任务id'
  job_id: number
  // '视频文件名'
  name: string
  // '渲染参数hash值'
  params_hash: string
  // '项目id'
  project_id: number
  //  '状态'
  status: 'rending' | 'done' | 'transcoding' | 'fail'
  // '缩略图'
  thumb_url: string
  // '更新时间'
  updated_time: number
  // '导出视频版本'
  version: number
  // '视频宽'
  width: number
  // 导出进度
  progress: number
}

export interface ProjectProgress {
  videos: ProjectProgressItem[] | null
}

// 项目详情接口的资源没有asset_id字段，用的是id
export type ProjectInfoAssetModel = Omit<ProjectAssetsModel, 'asset_id'>

export interface ProjectInfoModel {
  assets: null | ProjectInfoAssetModel[]
  project: null | ProjectModel
}

export interface GetProjectInfoParams {
  id?: ProjectModel['id']
}

export type SaveProjectInfoParams = Pick<
  ProjectModel,
  | 'duration'
  | 'id'
  | 'name'
  | 'ratio'
  | 'orientation_adapter'
  | 'last_stage'
  | 'config'
> & { tracks: Track[] }

export type ExportProjectProgressInfoParams = {
  id: number
}

export type ExportProjectInfoParams = {
  id: number
  name: string
  bit_rate: number
  ratios: RatioTypeUnion[]
  languages: string[]
  resolution: Resolution
}

export type ExportRendersProjectInfoParams = {
  project_id: number
  name: string
  bit_rate: number
  ratios: RatioTypeUnion[]
  resolution: Resolution
}

export type AddSegmentParams =
  | {
      segment_type: SegmentType.Fragment
      project_id: ProjectModel['id']
      stage: ProjectMode
    }
  | {
      // 资源组id。asset_id，asset_group_id 至少传一个
      asset_group_id: AssetGroup['id']
      project_id: ProjectModel['id']
      type: AssetType
      text?: string[]
      stage: ProjectMode
    }
  | {
      // 资源组id。asset_id，asset_group_id 至少传一个
      asset_ids: ProjectAssetsModel['asset_id'][]
      project_id: ProjectModel['id']
      type: AssetType
      text?: string[]
      stage: ProjectMode
    }

export interface UpdateProjectCoverParams {
  id: number
  file_base64: string
}

export interface BatchRenderParams {
  project_id: ProjectModel['id']
  // 优选数量
  num: number
  // 相同素材是否参与组合
  same: boolean
  ratios: string
  languages: string
}

export interface BatchRenderProjectGroup {
  asset_id: number
  group_id: number
  group_name: string
  pit_location: ProjectSegmentModel['pit_location']
}

export interface BatchRenderProjectModel extends ProjectModel {
  /** 相同资源是否参与组合 */
  has_equal_asset: boolean
  identify: string
  status: ExportStatus
  /** 智能优选 */
  optimization: boolean
  groups: Array<BatchRenderProjectGroup> | null
  file_url: string
  lang: string
  main_ratio: RatioTypeUnion
}

export interface BatchRenderModel {
  assets: null | ProjectInfoAssetModel[]
  render_list: BatchRenderProjectModel[]
  project_name: string
}

export interface SubmitBatchRenderParams {
  bit_rate?: number
  id: ProjectModel['id']
  name?: string
  renders: BatchRenderProjectModel[]
  resolution: Resolution
  ratios: RatioTypeUnion[]
  task_ids: AiTaskModel['id'][]
}

export interface RenderStatusModel {
  file_url: string
  identify: string
  status: ExportStatus
}

export type BatchRenderStatusModel = RenderStatusModel[] | null

/** 用户套餐 */
export type Plan = Pick<UserPlan, 'downloads_and_push' | 'video_credits'>

export interface SpeakerVoice {
  style: string
  role: string
  model: SpeechVoiceModel['model']
  name: SpeechVoiceModel['name']
  gender: SpeechVoiceModel['gender']
  selectedGender: Gender | '' | undefined
  language: string
  clone: false
}
export interface SpeakerClone {
  clone: true
}

export interface SpeakerUserUpload {
  type: SpeakerType.UserUpload
  voice_id: VoiceItem['id']
  clone: true
}

// 空态
// eslint-disable-next-line @typescript-eslint/ban-types
export type SpeakerVoiceUnset = {}

export type Speaker =
  | SpeakerVoice
  | SpeakerClone
  | SpeakerVoiceUnset
  | SpeakerUserUpload
export type SettledSpeakerVoice = SpeakerVoice | SpeakerClone

export type SpeakerLanguageVoice = Record<string, Speaker>
export type SpeakerCollection = Record<string, SpeakerLanguageVoice>

/**
 * 音频分离出来的资源
 */
export interface VocalSeparation {
  /** 分离出来的人声音频 */
  vocals_url: string
  /** 分离出来的bgm音频 */
  bgm_url: string
}

export enum AiRemoveItemType {
  // Ai消除
  Remove = 'remove',
  // 文本替换
  Replace = 'replace',
  // 文本翻译
  Translate = 'translate',
  // 字幕翻译
  SubtitleTranslate = 'subtitle_translate',
}
export interface AiRemoveItem {
  id: string
  // frame index: [start index, end index], item value start of 0
  range: [number, number]

  rect: Rect

  imageUrl: string
  ocrtext: string
  fps: number
  // AiRemoveItemType.Replace 文本组元件id
  // AiRemoveItemType.Translate 普通文本元件id
  segmentId: Segment['id']

  type: AiRemoveItemType
}

export type ProjectAssets = Record<
  ProjectAssetsModel['asset_id'],
  ProjectAssetsModel
>

export interface ProjectConfig {
  isRatioLinkage: boolean
  /** 元件开启多语言配置 */
  enableLanguagesSegmentIds: Array<ProjectSegmentModel['id']>
  activeLanguage: string
  /** 保存修改过的元件id，用于翻译使用 */
  changedSegmentsIds: Array<ProjectSegmentModel['id']>
  /** 保存需要更新的元件id，用于多语言更新配音使用 */
  changedLanguagesSegmentsIds: Array<ProjectSegmentModel['id']>
  // 保存变化的原字幕元件id，原字幕更新配音会检查该字段
  changedDefaultLanguagesSegmentsIds: Array<ProjectSegmentModel['id']>
  speaker: Record<ProjectSegmentModel['id'], SpeakerCollection>
  /** 主尺寸 */
  mainRatio: RatioTypeUnion
  /** 多语言设置替换的视频资源 */
  languagesReplaceAssetIds: Array<SegmentMaterial['asset_id']>

  /** 首次多比例裂变 */
  templateRatioId: number

  // Ai消除
  aiRemoveAssetIds: Array<ProjectAssetsModel['id']>
  aiRemove: Record<
    Segment['id'],
    {
      // 部分消除后的资源
      new_asset_id: Segment['asset_id']
      // 全量消除后的资源
      new_asset_id_all: Segment['asset_id']
      status: AiTaskStatus | ''
      // 部分消除任务
      taskId?: AiTaskModel['id']
      // 全量消除任务
      taskIdAll?: AiTaskModel['id']
      // 前置任务关系表（所依赖的任务的id）
      preTaskIds?: Record<AiTaskModel['id'], AiTaskModel['id']>
      progress: number
      key?: string
      // 聚合消除信息使用
      groupId?: string
      // 原始资源信息
      asset_id: Segment['asset_id']
      videoWidth: number
      videoHeight: number
      items: Array<AiRemoveItem>
    }
  >
  task: Array<Task>
  /** 智能识别合并的元件 */
  combineIds: Array<ProjectSegmentModel['id']>
  /** 视频编辑器所有字幕是否开启多语言 */
  enableAllSubtitleLanguages: boolean
}

export type AiRemoveConfig =
  ProjectConfig['aiRemove'][keyof ProjectConfig['aiRemove']]

export interface ProjectConfigExtra {
  async_task_ids: (Task['taskId'] | AiTaskModel['id'])[]
}

export interface updateProjectConfigParams {
  id: ProjectModel['id']
  config: string
}

export interface ProjectConfigModel {
  data: string
}

export interface TranslateSegmentsParams {
  is_combine: boolean
  /** 子元件是否跟随组的时间限制，为true时不跟 */
  slice?: boolean
  project_id: number
  batch: Array<{ id: SegmentCollection['id']; languages: string[] }>
  // 为true的时候，字幕只进行翻译文本
  only_translate_subtitle: boolean
}

/** 人声分离 */
export type VocalSeparations = Record<Segment['asset_id'], VocalSeparation>

/** 字幕生成的音频 tts */
interface TranslateResult {
  asset: ProjectAssetsModel
  text: string
  success: boolean
  error_code?: TranslateError
  // 快速克隆生成的音色id
  voice_id?: VoiceItem['id']
}

type TranslateResults = Record<string, TranslateResult>

interface TranslateSubtitleSegment {
  start: number
  end: number
  text: string
  speaker: string
}

export interface TranslateSegmentsResult {
  text_segments: Record<
    SegmentText['id'],
    Record<string, { success: boolean; text: string }>
  > | null

  video_segments: Record<
    SegmentVideo['id'],
    {
      success: boolean
      subtitle_segments: Array<
        TranslateSubtitleSegment & {
          translate_results: TranslateResults
        }
      >
    }
  > | null

  subtitle_segments: Record<
    SegmentText['id'],
    {
      text: string
      speaker: string
      translate_results: TranslateResults
    }
  > | null

  combine_segment: {
    source: string
    subtitle_segments: Array<TranslateSubtitleSegment> | null
  }

  speaker_config?: Record<ProjectSegmentModel['id'], SpeakerCollection>
}

export interface updateSpeechParams {
  batch: {
    id: number
    segment_ids: Array<ProjectSegmentModel['id']>
  }[]
  is_combine?: boolean
  project_id: number
}

export interface updateSpeechResult {
  [key: number]: {
    error: string
    asset: ProjectAssetsModel
    text: string
    success: boolean
    error_code?: TranslateError
    // 快速克隆生成的音色id
    voice_id?: VoiceItem['id']
  }
}

export interface VocalSeparationParams {
  batch: Array<{
    ids: Array<ProjectSegmentModel['id']>
  }>
  project_id: ProjectModel['id']
}

export interface VocalSeparationResult {
  batch_task_ids: number[]
}

export interface ParserSubtitle {
  end: number
  start: number
  text: string
}
export interface ParserSubtitleFile {
  subtitles: ParserSubtitle[]
}

export interface CloneLang {
  code: string
  name_en: string
  name: string
  name_jp: string
}
